import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Radar, Loader2, AlertTriangle, Car, Gauge, Timer, Phone, MessageSquare,
  TrendingUp, TrendingDown, Wrench, ShieldCheck, BadgeEuro, Percent, CalendarClock,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { formatEUR } from "../../lib/format";

/**
 * Fleet Radar (v4.1.0) — Fleet Intelligence & Operations.
 * Tabs: On-Road Tracker · Parked Days Tracker · Service & Alerts.
 */

const TABS = [
  { key: "on-road", label: "On-Road Tracker" },
  { key: "parked",  label: "Parked Days Tracker" },
  { key: "service", label: "Service & Alerts" },
];

const euDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return d ? `${d}/${m}/${y}` : iso;
};

const countdown = (hours) => {
  if (hours === null || hours === undefined) return "—";
  if (hours < 0) return `${Math.abs(Math.round(hours))}h overdue`;
  if (hours < 48) return `${Math.round(hours)}h left`;
  return `${Math.round(hours / 24)}d left`;
};

export default function FleetRadar() {
  const [tab, setTab] = useState("on-road");
  const [onRoad, setOnRoad] = useState(null);
  const [parked, setParked] = useState(null);
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/fleet-radar/on-road-tracker").then((r) => setOnRoad(r.data)),
      api.get("/admin/fleet-radar/parked-days-tracker").then((r) => setParked(r.data)),
      api.get("/admin/fleet-radar/service-alerts").then((r) => setService(r.data)),
    ])
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-fleet-radar-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400 inline-flex items-center gap-2">
            <Radar size={13} /> Fleet intelligence
          </p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
            Fleet <em className="italic">radar</em>.
          </h1>
        </div>
        <button
          onClick={load}
          className="border border-black/15 dark:border-white/15 px-5 py-2.5 text-sm rounded hover:border-black dark:hover:border-white transition-colors"
          data-testid="fr-refresh-btn"
        >
          Refresh data
        </button>
      </div>

      <div className="flex gap-1 border-b border-black/10 dark:border-white/10 mb-7 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? "border-black dark:border-white font-semibold"
                : "border-transparent text-neutral-500 hover:text-black dark:hover:text-white"
            }`}
            data-testid={`fr-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-24 flex justify-center" data-testid="fr-loading">
          <Loader2 className="animate-spin text-neutral-400" />
        </div>
      ) : (
        <>
          {tab === "on-road" && <OnRoadTab data={onRoad} />}
          {tab === "parked"  && <ParkedTab data={parked} onReload={load} />}
          {tab === "service" && <ServiceTab data={service} />}
        </>
      )}
    </div>
  );
}

/* ── Stat card ─────────────────────────────────────────────────────────── */
function Stat({ icon, label, value, sub, tone = "" }) {
  return (
    <div className={`border rounded-xl p-5 ${tone || "border-black/10 dark:border-white/10"}`}>
      <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 inline-flex items-center gap-1.5">
        {icon} {label}
      </p>
      <p className="font-display text-3xl tabular-nums mt-2">{value}</p>
      {sub && <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{sub}</p>}
    </div>
  );
}

/* ── 1 · On-Road Tracker ───────────────────────────────────────────────── */
const ROAD_FILTERS = [
  { key: "all",      label: "All" },
  { key: "on_road",  label: "On Road" },
  { key: "overdue",  label: "Overdue" },
  { key: "today",    label: "Returning Today" },
];

function OnRoadTab({ data }) {
  const [filter, setFilter] = useState("all");
  const rows = data?.vehicles || [];

  const visible = useMemo(() => rows.filter((r) => {
    if (filter === "overdue") return r.is_overdue;
    if (filter === "today")   return r.returning_today;
    if (filter === "on_road") return !r.is_overdue;
    return true;
  }), [rows, filter]);

  return (
    <div data-testid="fr-on-road-tab">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Stat icon={<Car size={12} />} label="Fleet total" value={data?.fleet_total ?? 0} sub="Car groups tracked" />
        <Stat icon={<Gauge size={12} />} label="On road" value={data?.on_road_count ?? 0} sub="Vehicles in customer custody" />
        <Stat
          icon={<AlertTriangle size={12} />}
          label="Overdue"
          value={data?.overdue_count ?? 0}
          sub="Past scheduled return"
          tone={data?.overdue_count ? "border-rose-300 bg-rose-50 dark:bg-rose-950/20" : ""}
        />
        <Stat icon={<CalendarClock size={12} />} label="Returning today" value={data?.returning_today ?? 0} sub="Prepare handover desk" />
      </div>

      {data?.overdue_count > 0 && (
        <div
          className="bg-rose-50 border border-rose-300 text-rose-900 rounded-xl p-5 mb-6 flex items-start gap-3"
          data-testid="fr-overdue-banner"
        >
          <AlertTriangle size={20} className="mt-0.5" />
          <div>
            <p className="font-semibold">{data.overdue_count} rental(s) past their return time.</p>
            <p className="text-sm mt-1">Call the renters below to confirm the handover or extend the contract.</p>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        {ROAD_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3.5 py-1.5 text-xs rounded-full border transition-colors ${
              filter === f.key
                ? "bg-black text-white border-black dark:bg-white dark:text-black"
                : "border-black/15 dark:border-white/20 text-neutral-600 dark:text-neutral-300"
            }`}
            data-testid={`fr-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <Table
        testId="fr-on-road-table"
        head={["Plate", "Model", "Customer", "Phone", "Return", "Countdown", "Drop-off", "Actions"]}
        empty="No vehicles currently on the road."
        rows={visible.map((r) => ({
          key: r.booking_id,
          tone: r.is_overdue ? "bg-rose-50/70 dark:bg-rose-950/20" : "",
          cells: [
            <span className="font-mono text-sm">{r.license_plate}</span>,
            <span>{r.make} {r.model}<span className="block text-xs text-neutral-500 font-mono">{r.booking_ref}</span></span>,
            r.customer_name,
            <span className="font-mono text-xs">{r.phone || "—"}</span>,
            <span className="tabular-nums">{euDate(r.dropoff_date)} {r.dropoff_time}</span>,
            <span className={`text-xs font-semibold px-2 py-1 rounded ${
              r.is_overdue ? "bg-rose-200 text-rose-900" : r.returning_today ? "bg-amber-100 text-amber-800" : "bg-neutral-100 text-neutral-700"
            }`}>{countdown(r.hours_remaining)}</span>,
            r.dropoff_location,
            <span className="flex gap-2">
              <a
                href={`tel:${r.phone}`}
                className="p-1.5 border border-black/15 rounded hover:border-black"
                title="Call renter"
                data-testid={`fr-call-${r.booking_ref}`}
              ><Phone size={13} /></a>
              <a
                href={`sms:${r.phone}`}
                className="p-1.5 border border-black/15 rounded hover:border-black"
                title="SMS renter"
                data-testid={`fr-sms-${r.booking_ref}`}
              ><MessageSquare size={13} /></a>
            </span>,
          ],
        }))}
      />
    </div>
  );
}

/* ── 2 · Parked Days Tracker ───────────────────────────────────────────── */
function ParkedTab({ data, onReload }) {
  const [applying, setApplying] = useState("");

  const applyDiscount = async (vehicleId) => {
    setApplying(vehicleId);
    try {
      const { data: res } = await api.post("/admin/fleet-radar/apply-discount", {
        vehicle_id: vehicleId, discount_pct: 10,
      });
      toast.success(`Radar discount applied — €${res.old_rate} → €${res.new_rate}/day`);
      onReload?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setApplying("");
    }
  };

  return (
    <div data-testid="fr-parked-tab">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Stat icon={<Percent size={12} />} label="Fleet utilization" value={`${data?.utilization_rate ?? 0}%`} sub={`${data?.active_rentals ?? 0} of ${data?.fleet_total ?? 0} rented`} />
        <Stat icon={<Car size={12} />} label="Active rentals" value={data?.active_rentals ?? 0} sub="Currently on road" />
        <Stat icon={<Timer size={12} />} label="Parked cars" value={data?.parked_count ?? 0} sub="Idle and bookable" />
        <Stat
          icon={<AlertTriangle size={12} />}
          label={`Idle > ${data?.alert_threshold_days ?? 7} days`}
          value={data?.flagged_count ?? 0}
          sub="Candidates for a price drop"
          tone={data?.flagged_count ? "border-amber-300 bg-amber-50 dark:bg-amber-950/20" : ""}
        />
      </div>

      <h2 className="text-sm uppercase tracking-[0.2em] text-neutral-600 dark:text-neutral-300 font-semibold mb-3">
        Parked duration
      </h2>
      <Table
        testId="fr-parked-table"
        head={["Plate", "Model", "Branch", "Daily rate", "Last returned", "Parked days", "Action"]}
        empty="Every vehicle is currently rented."
        rows={(data?.parked_vehicles || []).map((p) => ({
          key: p.vehicle_id,
          tone: p.needs_action ? "bg-amber-50/70 dark:bg-amber-950/20" : "",
          cells: [
            <span className="font-mono text-sm">{p.license_plate}</span>,
            `${p.make} ${p.model}`,
            p.branch,
            formatEUR(p.daily_rate),
            euDate(p.last_returned),
            <span className={`text-xs font-semibold px-2 py-1 rounded ${
              p.needs_action ? "bg-amber-200 text-amber-900" : "bg-neutral-100 text-neutral-700"
            }`}>{p.parked_days ?? "—"} day{p.parked_days === 1 ? "" : "s"}</span>,
            p.needs_action ? (
              <button
                onClick={() => applyDiscount(p.vehicle_id)}
                disabled={applying === p.vehicle_id}
                className="text-xs font-semibold px-3 py-1.5 bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50 inline-flex items-center gap-1.5"
                data-testid={`fr-apply-discount-${p.license_plate}`}
              >
                {applying === p.vehicle_id ? <Loader2 size={12} className="animate-spin" /> : <TrendingDown size={12} />}
                Apply Radar Discount
              </button>
            ) : <span className="text-xs text-neutral-400">—</span>,
          ],
        }))}
      />

      <h2 className="text-sm uppercase tracking-[0.2em] text-neutral-600 dark:text-neutral-300 font-semibold mt-10 mb-3 inline-flex items-center gap-2">
        <BadgeEuro size={15} /> Revenue leaderboard
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Table
          testId="fr-revenue-table"
          head={["Model", "Days", "Revenue", "ADR"]}
          empty="No completed rentals yet."
          rows={(data?.revenue_leaderboard || []).map((e, i) => ({
            key: e.vehicle_id,
            cells: [
              <span>{i + 1}. {e.model}<span className="block text-xs text-neutral-500 font-mono">{e.license_plate}</span></span>,
              e.days_rented,
              formatEUR(e.revenue),
              formatEUR(e.adr),
            ],
          }))}
        />
        <Table
          testId="fr-category-table"
          head={["Category", "Rentals", "Days", "Revenue"]}
          empty="No category data yet."
          rows={(data?.category_leaderboard || []).map((c) => ({
            key: c.category,
            cells: [<span className="capitalize">{c.category}</span>, c.rentals, c.days_rented, formatEUR(c.revenue)],
          }))}
        />
      </div>
    </div>
  );
}

/* ── 3 · Service & Alerts ──────────────────────────────────────────────── */
function ServiceTab({ data }) {
  return (
    <div data-testid="fr-service-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Stat icon={<Wrench size={12} />} label="Vehicles flagged" value={data?.alert_count ?? 0} sub={`Next ${data?.window_days ?? 30} days`} />
        <Stat
          icon={<AlertTriangle size={12} />}
          label="Critical"
          value={data?.critical_count ?? 0}
          sub="Expired or due within 7 days"
          tone={data?.critical_count ? "border-rose-300 bg-rose-50 dark:bg-rose-950/20" : ""}
        />
        <Stat icon={<ShieldCheck size={12} />} label="Service window" value={`${(data?.km_window ?? 1000).toLocaleString()} km`} sub="Odometer alert threshold" />
      </div>

      {(data?.vehicles || []).length === 0 ? (
        <p className="text-sm text-neutral-500 py-16 text-center border border-dashed border-black/10 rounded-xl" data-testid="fr-service-empty">
          No service, KTEO or insurance alerts in the next {data?.window_days ?? 30} days.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="fr-service-grid">
          {(data?.vehicles || []).map((v) => (
            <div
              key={v.vehicle_id}
              className={`border rounded-xl p-5 ${
                v.severity === "critical"
                  ? "border-rose-300 bg-rose-50/60 dark:bg-rose-950/20"
                  : "border-amber-300 bg-amber-50/50 dark:bg-amber-950/20"
              }`}
              data-testid={`fr-service-card-${v.license_plate}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-mono text-sm">{v.license_plate}</p>
                  <p className="font-display text-xl mt-0.5">{v.model}</p>
                  <p className="text-xs text-neutral-500 mt-0.5">{v.branch} · {v.current_odometer.toLocaleString()} km</p>
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${
                  v.severity === "critical" ? "bg-rose-200 text-rose-900" : "bg-amber-200 text-amber-900"
                }`}>{v.severity}</span>
              </div>
              <ul className="mt-4 space-y-2">
                {v.alerts.map((a, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 text-sm">
                    <span className="inline-flex items-center gap-1.5 text-neutral-700 dark:text-neutral-200">
                      {a.type === "service_km" ? <Gauge size={13} /> : a.type === "kteo" ? <ShieldCheck size={13} /> : a.type === "insurance" ? <ShieldCheck size={13} /> : <Wrench size={13} />}
                      {a.label}
                    </span>
                    <span className={`text-xs font-semibold px-2 py-1 rounded tabular-nums ${
                      a.severity === "critical" ? "bg-rose-200 text-rose-900" : "bg-white/70 text-neutral-700"
                    }`}>
                      {a.type === "service_km"
                        ? `${a.km_remaining <= 0 ? "Overdue" : `${a.km_remaining.toLocaleString()} km`}`
                        : a.detail}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Shared table ──────────────────────────────────────────────────────── */
function Table({ head, rows, empty, testId }) {
  return (
    <div className="border border-black/10 dark:border-white/10 rounded-xl overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-neutral-50 dark:bg-white/5 text-left">
            {head.map((h) => (
              <th key={h} className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={head.length} className="px-4 py-10 text-center text-neutral-500">{empty}</td></tr>
          ) : rows.map((r) => (
            <tr key={r.key} className={`border-t border-black/5 dark:border-white/5 ${r.tone || ""}`}>
              {r.cells.map((c, i) => (
                <td key={i} className="px-4 py-3 align-middle whitespace-nowrap">{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
